import BaseController from "de/realcore/realseats/controller/BaseController";
import JSONModel from "sap/ui/model/json/JSONModel";
import Table from "sap/m/Table";
import Filter from "sap/ui/model/Filter";
import FilterOperator from "sap/ui/model/FilterOperator";
import ListBinding from "sap/ui/model/ListBinding";
import FilterBar from "sap/ui/comp/filterbar/FilterBar";
import type FilterItem from "sap/ui/comp/filterbar/FilterItem";
import SearchField from "sap/m/SearchField";
import ComboBox from "sap/m/ComboBox";
import DatePicker from "sap/m/DatePicker";
import formatter from "../src/utils/formatter";
import Log from "sap/base/Log";
import ApiService from "../src/api/ApiService";
import BusyIndicator from "sap/ui/core/BusyIndicator";
import type Event from "sap/ui/base/Event";
import Control from "sap/ui/core/Control";
import Fragment from "sap/ui/core/Fragment";
import Popover from "sap/m/Popover";
import MessageBox from "sap/m/MessageBox";
import Dialog from "sap/m/Dialog";
import UIFeedbackHelper from "../src/utils/UIFeedbackHelper";
import type { BookingModel } from "../src/types/";
import { getMaxBookingDate } from "../src/utils/TimeUtils";

/**
 * Booking data structure for the BookedForOthers view
 * We need to outsource this into a separate type file later
 */
/**
 * @namespace de.realcore.realseats.controller
 */
export default class BookedForOthers extends BaseController {
    private oBookingsForOthersModel!: JSONModel;
    public formatter = formatter;
    private authToken!: string;
    private oFilterBar!: FilterBar;
    private oTable!: Table;
    private ApiService: ApiService;
    private _roomTypesPopover: Popover;
    private cancelDialog: Dialog;

    public onInit(): void {
        this.getOwnerComponent()
            .getRouter()
            .getRoute("BookedForOthers")
            .attachPatternMatched(this._onRouteMatched.bind(this));

        this.ApiService = new ApiService(this.getOwnerComponent());

        const oView = this.getView();
        this.oFilterBar = oView?.byId("bookedForOthersFilterBar") as FilterBar;
        this.oTable = oView?.byId("idDataTable") as Table;

        // Initialize DialogData model for date picker constraints
        const dialogData = {
            minDate: new Date(),
            maxDate: new Date(getMaxBookingDate()) // Create fresh Date object to prevent mutation
        };
        const dialogDataModel = new JSONModel(dialogData);
        oView?.setModel(dialogDataModel, "DialogData");
    }

    public async getBookingsForOthers(): Promise<void> {
        try {
            // Initialize model with loading state
            if (!this.oBookingsForOthersModel) {
                this.oBookingsForOthersModel = new JSONModel({
                    bookingsForOthers: [],
                    isLoading: true
                });
                this.getView().setModel(this.oBookingsForOthersModel, "BookingsForOthersModel");
            } else {
                this.oBookingsForOthersModel.setProperty("/isLoading", true);
            }

            const bookingsForOthers = await this.ApiService.booking.fetchBookingsForOthers();

            // Filter out cancelled bookings completely - don't show them at all
            const nonCancelledBookings = bookingsForOthers.filter(
                (booking: any) => !booking.cancelled
            );

            // Add group information to each booking
            const now = new Date();
            const enhancedBookings = nonCancelledBookings.map((booking: any) => {
                const bookingDate = new Date(booking.startTime);
                const isPast = bookingDate < now;
                return {
                    ...booking,
                    bookingGroup: isPast ? "B - Past Bookings" : "A - Upcoming Bookings",
                    timeDistance: Math.abs(bookingDate.getTime() - now.getTime()) // Distance from now for sorting
                };
            });

            this.oBookingsForOthersModel.setData({
                bookingsForOthers: enhancedBookings,
                isLoading: false
            });

            this.onBookingsDataLoaded();
            BusyIndicator.hide();
        } catch (error) {
            UIFeedbackHelper.showErrorMessage(
                `An error occurred while fetching bookings for others: ${(error as Error).message}`
            );
            Log.error("Error fetching bookings for others:", error as Error);
            // Set loading to false even on error and show empty state
            if (this.oBookingsForOthersModel) {
                this.oBookingsForOthersModel.setData({
                    bookingsForOthers: [],
                    isLoading: false
                });
            }
            BusyIndicator.hide();
        }
    }

    private _onRouteMatched(): void {
        void this.getBookingsForOthers();
    }

    private onBookingsDataLoaded(): void {
        const oBookingTypeCombo = this.byId("idBookedForOthersTypeComboBox") as ComboBox;
        if (oBookingTypeCombo) {
            oBookingTypeCombo.setSelectedKey("all");
        }
        const oTableBinding = this.oTable.getBinding("items") as ListBinding;
        if (oTableBinding) {
            oTableBinding.filter([]);
        }
    }

    public onSearch(): void {
        const aFilterItems = this.oFilterBar.getAllFilterItems(true) as FilterItem[];
        const aTableFilters = aFilterItems.reduce((aResult: Filter[], oFilterItem: FilterItem) => {
            const sFilterName = oFilterItem.getName();
            const oFilterControl = oFilterItem.getControl() as SearchField | ComboBox | DatePicker;

            switch (sFilterName) {
                case "Name": {
                    const sValue = (oFilterControl as SearchField).getValue?.();
                    if (sValue) {
                        const aNameParts = sValue.trim().split(/\s+/);

                        const aPartFilters = aNameParts.map((part) => {
                            return new Filter({
                                filters: [
                                    new Filter("employee/firstName", FilterOperator.Contains, part),
                                    new Filter("employee/lastName", FilterOperator.Contains, part)
                                ],
                                and: false
                            });
                        });

                        const oCombinedFilter = new Filter({
                            filters: aPartFilters,
                            and: true
                        });

                        aResult.push(oCombinedFilter);
                    }
                    break;
                }

                case "BookingType": {
                    const control = oFilterControl as ComboBox;
                    const sValue = control.getSelectedKey?.();
                    if (sValue && sValue !== "all") {
                        const aTypeFilters = this.getBookingTypeFilters(sValue);
                        aResult.push(...aTypeFilters);
                    }
                    break;
                }
                case "Date": {
                    const sDate = (oFilterControl as DatePicker).getDateValue();
                    if (sDate instanceof Date && !isNaN(sDate.getTime())) {
                        const sStart = new Date(sDate);
                        sStart.setHours(0, 0, 0, 0);
                        const sEnd = new Date(sDate);
                        sEnd.setHours(23, 59, 59, 999);
                        aResult.push(
                            new Filter(
                                "startTime",
                                FilterOperator.BT,
                                sStart.toISOString(),
                                sEnd.toISOString()
                            )
                        );
                    }
                    break;
                }
                case "Room": {
                    const control = oFilterControl as SearchField;
                    const sValue = control.getValue?.();
                    if (sValue) {
                        aResult.push(new Filter("seat/name", FilterOperator.Contains, sValue));
                    }
                    break;
                }
                case "Seat": {
                    const control = oFilterControl as SearchField;
                    const sValue = control.getValue?.();
                    if (sValue) {
                        aResult.push(new Filter("seat", FilterOperator.Contains, sValue));
                    }
                    break;
                }
            }
            return aResult;
        }, []);

        const oTableBinding = this.oTable.getBinding("items") as ListBinding;
        oTableBinding.filter(aTableFilters);
    }

    private getBookingTypeFilters(sBookingType: string): Filter[] {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const sTodayISO = today.toISOString();
        const aFilters: Filter[] = [];

        switch (sBookingType) {
            case "currently":
                aFilters.push(
                    new Filter("cancelled", FilterOperator.EQ, false),
                    new Filter("startTime", FilterOperator.GE, sTodayISO)
                );
                break;

            case "history":
                aFilters.push(
                    new Filter("cancelled", FilterOperator.EQ, false),
                    new Filter("startTime", FilterOperator.LT, sTodayISO)
                );
                break;
        }

        return aFilters;
    }

    /**
     * Shows a popover with all room types for a room when the link is clicked
     * @param oEvent The press event
     */
    public async onShowRoomTypesPopover(oEvent: Event): Promise<void> {
        const oLink = oEvent.getSource() as Control;
        const oContext = oLink.getBindingContext("BookingsForOthersModel");

        if (!oContext) {
            return;
        }

        const oBooking = oContext.getObject() as BookingModel;
        const aRoomTypes = oBooking.seat?.room?.roomTypes || [];

        // Create popover if it doesn't exist
        if (!this._roomTypesPopover) {
            this._roomTypesPopover = (await Fragment.load({
                id: this.getView()?.getId(),
                name: "de.realcore.realseats.view.fragment.RoomTypesPopover",
                controller: this
            })) as Popover;
            this.getView()?.addDependent(this._roomTypesPopover);
        }

        // Set the room types data to the popover
        const oPopoverModel = new JSONModel({ roomTypes: aRoomTypes });
        this._roomTypesPopover.setModel(oPopoverModel, "PopoverRoomTypesModel");

        // Open popover
        this._roomTypesPopover.openBy(oLink);
    }

    /**
     * Handles the cancel booking button press - identical to MyBookings
     * @param oEvent The press event
     */
    onCancelBooking(oEvent: Event): void {
        const source = oEvent.getSource();

        if (!("getBindingContext" in source) || typeof source.getBindingContext !== "function") {
            Log.error("Event source does not support getBindingContext.");
            return;
        }

        const oSource = source as Control;
        const oContext = oSource.getBindingContext("BookingsForOthersModel");
        const oView = this.getView();

        if (!this.cancelDialog) {
            Fragment.load({
                id: "bookedForOthers",
                name: "de.realcore.realseats.view.fragment.CancelBookingDialog",
                controller: this
            })
                .then((oFragment) => {
                    if (!oFragment) {
                        Log.error("Fragment not loaded");
                        return;
                    }

                    this.cancelDialog = oFragment as Dialog;

                    // Set the booking data to the expected BookingsModel for the dialog
                    const oBooking = oContext?.getObject() as BookingModel;
                    const oDialogModel = new JSONModel(oBooking);
                    this.cancelDialog.setModel(oDialogModel, "BookingsModel");

                    oView?.addDependent(this.cancelDialog);
                    this.cancelDialog.open();
                })
                .catch((error) => {
                    Log.error("Failed to load the CancelBookingDialog fragment", error);
                });
        } else {
            // Set the booking data to the expected BookingsModel for the dialog
            const oBooking = oContext?.getObject() as BookingModel;
            const oDialogModel = new JSONModel(oBooking);
            this.cancelDialog.setModel(oDialogModel, "BookingsModel");
            this.cancelDialog.open();
        }
    }

    public onCancelDialog(): void {
        if (this.cancelDialog) {
            this.cancelDialog.close();
        }
    }

    /**
     * Handles confirmation of booking cancellation - identical to MyBookings
     * Uses the new API service for clean separation of concerns
     */
    public async onConfirmCancellation(): Promise<void> {
        const oDialog = this.cancelDialog;
        const oDialogModel = oDialog?.getModel("BookingsModel") as JSONModel;
        const oBooking = oDialogModel?.getData() as BookingModel;
        const bookingId = oBooking?.id;
        const cancelBookingDesc =
            oBooking?.cancelDescription || "No reason provided for cancellation.";

        try {
            await this.ApiService.booking.cancelBooking(bookingId, cancelBookingDesc);

            MessageBox.success("Die Buchung wurde erfolgreich storniert.");
            this.cancelDialog?.close();

            // Refresh the bookings list
            await this.getBookingsForOthers();
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
            MessageBox.error("Fehler beim Stornieren: " + errorMessage);
        }
    }
}
